import React, { useEffect, useState } from 'react';
import { loadScenicData } from '../data';
import './InteractiveDemo.css';

const DIMENSIONS = [
  { key: 'BM', label: '基础设施' },
  { key: 'Exc', label: '游览' },
  { key: 'Hyg', label: '卫生' },
  { key: 'PT', label: '邮政电信' },
  { key: 'REP', label: '声誉' },
  { key: 'TSA', label: '旅游安全' },
  { key: 'TSH', label: '旅游购物' },
  { key: 'TT', label: '交通运输' }
];

const DIFF_EPSILON = 0.002;

function getNumericValue(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (value === null || value === undefined || value === '' || value === '数据暂无') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatScore(value, digits = 3) {
  const numericValue = getNumericValue(value);
  return numericValue === null ? '—' : numericValue.toFixed(digits);
}

function formatSignedDiff(value) {
  const numericValue = getNumericValue(value);
  if (numericValue === null) return '—';
  if (numericValue === 0) return '0.000';
  return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(3)}`;
}

function isAboveAverage(value) {
  const numericValue = getNumericValue(value);
  return numericValue !== null && numericValue > DIFF_EPSILON;
}

function isNearAverage(value) {
  const numericValue = getNumericValue(value);
  return numericValue !== null && Math.abs(numericValue) <= DIFF_EPSILON;
}

function getOverallNote(diff, average, label) {
  if (average === null || diff === null) {
    return '暂无均值参考';
  }

  if (isAboveAverage(diff)) {
    return `${label} ${formatScore(average, 1)} · 高于均值 ${formatSignedDiff(diff)}`;
  }

  if (isNearAverage(diff)) {
    return `${label} ${formatScore(average, 1)} · 与均值相近`;
  }

  return `${label} ${formatScore(average, 1)} · 处于同类常见区间`;
}

function getDimensionStatus(diff) {
  if (diff === null) {
    return { text: '暂无参考', tone: 'neutral' };
  }

  if (isAboveAverage(diff)) {
    return { text: `高于均值 ${formatSignedDiff(diff)}`, tone: 'positive' };
  }

  if (isNearAverage(diff)) {
    return { text: '与均值相近', tone: 'neutral' };
  }

  return { text: '处于同类常见区间', tone: 'neutral' };
}

function calculateAverage(values) {
  if (!values.length) return null;
  const total = values.reduce((sum, current) => sum + current, 0);
  return total / values.length;
}

function computeRankPosition(items, getValue, targetId) {
  const rankedItems = items
    .map(item => ({
      id: item.id,
      value: getNumericValue(getValue(item))
    }))
    .filter(item => item.value !== null)
    .sort((a, b) => b.value - a.value);

  let rank = 0;
  let lastValue = null;

  for (let index = 0; index < rankedItems.length; index += 1) {
    const currentItem = rankedItems[index];
    if (currentItem.value !== lastValue) {
      rank = index + 1;
      lastValue = currentItem.value;
    }

    if (currentItem.id === targetId) {
      return rank;
    }
  }

  return null;
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, value));
}

const InteractiveDemo = () => {
  const [allScenicData, setAllScenicData] = useState({ scenicAreas: [], scenicAreasByCategory: {} });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArea, setSelectedArea] = useState(null);
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await loadScenicData();
        setAllScenicData(data);

        if (data.scenicAreas && data.scenicAreas.length > 0) {
          setFilteredAreas(data.scenicAreas);
          setSelectedArea(data.scenicAreas[0]);
        } else {
          setFilteredAreas([]);
          setSelectedArea(null);
          if (!data.scenicAreas || data.scenicAreas.length === 0) {
            console.warn('No scenic areas loaded after fetch.');
          }
        }

        setIsLoading(false);
      } catch (fetchError) {
        console.error('Error fetching scenic data in component:', fetchError);
        setError('加载景区数据失败，请检查CSV文件和网络连接。');
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (allScenicData.scenicAreas.length === 0 && !isLoading) return;

    const areasToShow = selectedCategory === 'all'
      ? allScenicData.scenicAreas
      : allScenicData.scenicAreasByCategory[selectedCategory] || [];

    setFilteredAreas(areasToShow);

    if (areasToShow.length > 0) {
      if (!selectedArea || !areasToShow.find(area => area.id === selectedArea.id)) {
        setSelectedArea(areasToShow[0]);
      }
    } else {
      setSelectedArea(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, allScenicData.scenicAreas, allScenicData.scenicAreasByCategory, isLoading]);

  const currentSelectedArea = selectedArea;
  const comparisonAreas = filteredAreas.length > 0 ? filteredAreas : allScenicData.scenicAreas;
  const comparisonCount = comparisonAreas.length;
  const comparisonLabel = selectedCategory === 'all'
    ? `全部景区 (${comparisonCount})`
    : `${selectedCategory} (${comparisonCount})`;
  const averageLabel = selectedCategory === 'all' ? '全样本均值' : '当前类别均值';

  let overallScore = null;
  let overallAverage = null;
  let overallDiff = null;
  let overallRankInScope = null;
  let dimensionsAboveAverage = 0;
  let topTenDimensions = 0;
  let bestGlobalRank = null;
  let highlightedDimensions = [];
  let orderedDimensions = [];
  let overviewItems = [];
  let summaryText = '选择景区后，可从亮点维度和整体表现中快速理解其质量特征。';

  if (currentSelectedArea) {
    overallScore = getNumericValue(currentSelectedArea.rank);
    const overallValues = comparisonAreas
      .map(area => getNumericValue(area.rank))
      .filter(value => value !== null);
    overallAverage = calculateAverage(overallValues);
    overallDiff = overallScore !== null && overallAverage !== null ? overallScore - overallAverage : null;
    overallRankInScope = computeRankPosition(comparisonAreas, area => area.rank, currentSelectedArea.id);

    const dimensionInsights = DIMENSIONS.map(dimension => {
      const score = getNumericValue(currentSelectedArea.scores?.[dimension.key]);
      const comparisonValues = comparisonAreas
        .map(area => getNumericValue(area.scores?.[dimension.key]))
        .filter(value => value !== null);
      const average = calculateAverage(comparisonValues);
      const minimum = comparisonValues.length ? Math.min(...comparisonValues) : null;
      const maximum = comparisonValues.length ? Math.max(...comparisonValues) : null;
      const diff = score !== null && average !== null ? score - average : null;
      const localRank = computeRankPosition(
        comparisonAreas,
        area => area.scores?.[dimension.key],
        currentSelectedArea.id
      );
      const globalRank = currentSelectedArea.dimensionRanks?.[dimension.key];
      const range = minimum !== null && maximum !== null ? maximum - minimum : 0;
      const scorePercent = score !== null && minimum !== null && maximum !== null
        ? clampPercent(range === 0 ? 100 : ((score - minimum) / range) * 100)
        : 0;
      const averagePercent = average !== null && minimum !== null && maximum !== null
        ? clampPercent(range === 0 ? 50 : ((average - minimum) / range) * 100)
        : 0;

      return {
        ...dimension,
        score,
        average,
        diff,
        minimum,
        maximum,
        localRank,
        globalRank,
        scorePercent,
        averagePercent
      };
    }).filter(dimension => dimension.score !== null);

    highlightedDimensions = dimensionInsights
      .filter(dimension => isAboveAverage(dimension.diff))
      .sort((a, b) => (b.diff - a.diff) || (b.score - a.score));
    dimensionsAboveAverage = highlightedDimensions.length;

    const neutralDimensions = DIMENSIONS
      .map(dimension => dimensionInsights.find(item => item.key === dimension.key))
      .filter(dimension => dimension && !isAboveAverage(dimension.diff));

    orderedDimensions = [...highlightedDimensions, ...neutralDimensions];

    const globalRanks = dimensionInsights
      .map(dimension => dimension.globalRank)
      .filter(rank => typeof rank === 'number');

    topTenDimensions = globalRanks.filter(rank => rank <= 10).length;
    bestGlobalRank = globalRanks.length ? Math.min(...globalRanks) : null;

    if (highlightedDimensions.length > 0) {
      const highlightNames = highlightedDimensions
        .slice(0, 3)
        .map(dimension => dimension.label)
        .join('、');

      summaryText = `${currentSelectedArea.name}在${comparisonLabel}中有 ${highlightedDimensions.length} 个维度高于${averageLabel}${highlightNames ? `，亮点主要集中在${highlightNames}` : ''}，其余维度整体处于同类相近区间。`;
    } else {
      summaryText = `${currentSelectedArea.name}整体与${averageLabel}接近，八维表现较为均衡，整体处于同类景区常见区间。`;
    }

    const remainingDimensions = dimensionInsights.length - highlightedDimensions.length;
    overviewItems = [
      overallDiff !== null && isAboveAverage(overallDiff)
        ? `总体评分高于${averageLabel} ${formatSignedDiff(overallDiff)}，在当前范围中位列第 ${overallRankInScope || '—'}。`
        : isNearAverage(overallDiff)
          ? `总体评分与${averageLabel}相近，在当前范围中呈现稳定状态。`
          : `总体评分处于${comparisonLabel}常见区间，整体表现较为平稳。`,
      remainingDimensions > 0
        ? `其余 ${remainingDimensions} 个维度以中性方式呈现，说明整体表现稳定，可持续优化。`
        : '八个维度均高于当前参考均值，整体亮点较为突出。',
      bestGlobalRank
        ? `最佳亮点维度位列全样本第 ${bestGlobalRank} 名；进入全样本前十的维度共有 ${topTenDimensions} 个。`
        : '当前看板优先突出亮点维度，其余维度保留均值参考线作中性比较。'
    ];
  }

  const handleAreaChange = event => {
    const areaId = parseInt(event.target.value, 10);
    const area = allScenicData.scenicAreas.find(item => item.id === areaId);
    if (area) setSelectedArea(area);
  };

  const handleCategoryChange = event => {
    setSelectedCategory(event.target.value);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>正在加载景区数据...</p>
      </div>
    );
  }

  if (error) {
    return <div className="error-container"><p>{error}</p></div>;
  }

  if (allScenicData.scenicAreas.length === 0) {
    return (
      <div className="error-container">
        <p>没有景区数据可显示。请确保CSV文件已正确放置在public目录并包含有效数据。</p>
      </div>
    );
  }

  return (
    <section id="demo" className="interactive-demo">
      <div className="demo-heading">
        <p className="demo-kicker">MSAQE Interactive Lab</p>
        <h2><i className="fas fa-chart-line"></i> <span>景区质量评估演示</span></h2>
        <p className="demo-description">
          优先呈现高于均值的亮点维度，其余维度采用更温和的中性表达，便于直观理解整体特征。
        </p>
      </div>

      <div className="filters-container">
        <div className="filter-group">
          <label htmlFor="scenic-category"><i className="fas fa-layer-group"></i> 对比范围</label>
          <select
            id="scenic-category"
            value={selectedCategory}
            onChange={handleCategoryChange}
            className="select-styled"
            disabled={isLoading || Object.keys(allScenicData.scenicAreasByCategory).length === 0}
          >
            <option value="all">所有景区 ({allScenicData.scenicAreas.length})</option>
            {Object.keys(allScenicData.scenicAreasByCategory).map(category => (
              <option key={category} value={category}>
                {category} ({allScenicData.scenicAreasByCategory[category] ? allScenicData.scenicAreasByCategory[category].length : 0})
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group filter-wide">
          <label htmlFor="scenic-area"><i className="fas fa-map-marked-alt"></i> 景区对象</label>
          <select
            id="scenic-area"
            value={currentSelectedArea ? currentSelectedArea.id : ''}
            onChange={handleAreaChange}
            className="select-styled"
            disabled={isLoading || filteredAreas.length === 0}
          >
            {filteredAreas.map(area => (
              <option key={area.id} value={area.id}>
                {area.name} (总体评分: {area.rank})
              </option>
            ))}
          </select>
        </div>
      </div>

      {currentSelectedArea && (
        <div className="demo-dashboard">
          <div className="hero-panel">
            <div className="hero-image-wrap">
              <img src={currentSelectedArea.image} alt={currentSelectedArea.name} className="hero-image" />
            </div>
            <div className="hero-content">
              <div className="hero-badges">
                <span className="hero-badge category-badge">{currentSelectedArea.category}</span>
                <span className="hero-badge scope-badge">分析范围: {comparisonLabel}</span>
              </div>
              <h3>{currentSelectedArea.name}</h3>
              <p className="hero-summary">{summaryText}</p>

              <div className="summary-metrics">
                <article className="metric-card">
                  <span className="metric-label">总体评分</span>
                  <strong className="metric-value">{formatScore(overallScore, 1)}</strong>
                  <span className={`metric-note ${isAboveAverage(overallDiff) ? 'positive' : 'neutral'}`}>
                    {getOverallNote(overallDiff, overallAverage, averageLabel)}
                  </span>
                </article>
                <article className="metric-card">
                  <span className="metric-label">当前范围位次</span>
                  <strong className="metric-value">{overallRankInScope ? `第 ${overallRankInScope}` : '—'}</strong>
                  <span className="metric-note">共 {comparisonCount} 个景区</span>
                </article>
                <article className="metric-card">
                  <span className="metric-label">高于均值维度</span>
                  <strong className="metric-value">{dimensionsAboveAverage}/8</strong>
                  <span className="metric-note">越多说明越均衡</span>
                </article>
                <article className="metric-card">
                  <span className="metric-label">全样本前十维度</span>
                  <strong className="metric-value">{topTenDimensions}</strong>
                  <span className="metric-note">
                    {bestGlobalRank ? `最佳维度全样本第 ${bestGlobalRank} 名` : '暂无全样本排名'}
                  </span>
                </article>
              </div>
            </div>
          </div>

          <div className="insight-panels">
            <article className="insight-card insight-strong">
              <div className="insight-header">
                <span className="insight-tag">Highlights</span>
                <h4>亮点维度</h4>
              </div>
              {highlightedDimensions.length > 0 ? (
                <ul className="insight-list">
                  {highlightedDimensions.map(dimension => (
                    <li key={dimension.key}>
                      <div>
                        <span className="insight-name">{dimension.label}</span>
                        <span className="insight-sub positive">
                          高于{averageLabel} {formatSignedDiff(dimension.diff)}
                        </span>
                      </div>
                      <strong>{formatScore(dimension.score)}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="insight-empty">
                  当前没有明显高于均值的单项维度，整体表现更接近均衡状态。
                </p>
              )}
            </article>

            <article className="insight-card insight-overview">
              <div className="insight-header">
                <span className="insight-tag">Overview</span>
                <h4>整体观察</h4>
              </div>
              <ul className="overview-list">
                {overviewItems.map(item => (
                  <li key={item}>
                    <span className="overview-dot"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <div className="dimension-board">
            <div className="board-heading">
              <div>
                <p className="board-kicker">Dimension Ladder</p>
                <h4>八维表现全景</h4>
              </div>
              <p className="board-description">
                优先展示高于均值的亮点维度，其余维度保持中性呈现，不单独放大低于均值的项目。
              </p>
            </div>

            <div className="dimension-grid">
              {orderedDimensions.map(dimension => {
                const status = getDimensionStatus(dimension.diff);

                return (
                  <article
                    key={dimension.key}
                    className={`dimension-card ${isAboveAverage(dimension.diff) ? 'is-positive' : 'is-neutral'}`}
                  >
                    <div className="dimension-card-top">
                      <div>
                        <div className="dimension-title-wrap">
                          {isAboveAverage(dimension.diff) && <span className="dimension-badge">亮点</span>}
                          <h5>{dimension.label}</h5>
                        </div>
                        <p className="dimension-caption">
                          {dimension.localRank
                            ? `组内第 ${dimension.localRank} / ${comparisonCount}`
                            : '组内位次暂无'}
                        </p>
                      </div>
                      <div className="dimension-value-wrap">
                        <strong>{formatScore(dimension.score)}</strong>
                        <span className={status.tone}>{status.text}</span>
                      </div>
                    </div>

                    <div className="dimension-track">
                      <div
                        className="dimension-bar"
                        style={{ width: `${dimension.scorePercent}%` }}
                      ></div>
                      <div
                        className="dimension-average-marker"
                        style={{ left: `${dimension.averagePercent}%` }}
                      >
                        <span>{averageLabel}</span>
                      </div>
                    </div>

                    <div className="dimension-meta">
                      <span>{averageLabel} {formatScore(dimension.average)}</span>
                      <span>区间 {formatScore(dimension.minimum)} - {formatScore(dimension.maximum)}</span>
                      <span>
                        {typeof dimension.globalRank === 'number'
                          ? `全样本第 ${dimension.globalRank} 名`
                          : '全样本排名暂无'}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!currentSelectedArea && !isLoading && !error && (
        <div className="error-container">
          <p>请选择一个景区查看详情，或当前分类下无景区数据。</p>
        </div>
      )}
    </section>
  );
};

export default InteractiveDemo;
